import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ConnectionState {
  isConnected: boolean;
  lastConnectionTime: string | null;
  reconnectAttempts: number;
  countdownSeconds: number | null;
  isReconnecting: boolean;
  lastError: string | null;
  pendingData: any[];
}

const initialState: ConnectionState = {
  isConnected: true,
  lastConnectionTime: new Date().toISOString(),
  reconnectAttempts: 0,
  countdownSeconds: null,
  isReconnecting: false,
  lastError: null,
  pendingData: []
};

const connectionSlice = createSlice({
  name: 'connection',
  initialState,
  reducers: {
    setConnected: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
      if (action.payload) {
        state.lastConnectionTime = new Date().toISOString();
        state.reconnectAttempts = 0;
        state.countdownSeconds = null;
        state.isReconnecting = false;
        state.lastError = null;
      }
    },
    
    setDisconnected: (state, action: PayloadAction<{ error?: string }>) => {
      state.isConnected = false;
      state.lastError = action.payload.error || 'Connection lost';
      // Start 60 second countdown
      state.countdownSeconds = 60;
    },
    
    updateCountdown: (state, action: PayloadAction<number>) => {
      state.countdownSeconds = action.payload;
    },
    
    incrementReconnectAttempts: (state) => {
      state.reconnectAttempts += 1;
      state.isReconnecting = true;
    },

    resetReconnectAttempts: (state) => {
      // Used when we need to restart the reconnect cycle without claiming
      // the connection is restored (e.g. countdown expired but session is
      // still valid — we keep retrying from attempt 0 instead of logging out).
      state.reconnectAttempts = 0;
    },

    setReconnecting: (state, action: PayloadAction<boolean>) => {
      state.isReconnecting = action.payload;
    },
    
    addPendingData: (state, action: PayloadAction<any>) => {
      state.pendingData.push({
        ...action.payload,
        timestamp: new Date().toISOString()
      });
    },
    
    clearPendingData: (state) => {
      state.pendingData = [];
    },
    
    resetConnection: (state) => {
      return initialState;
    }
  }
});

export const {
  setConnected,
  setDisconnected,
  updateCountdown,
  incrementReconnectAttempts,
  resetReconnectAttempts,
  setReconnecting,
  addPendingData,
  clearPendingData,
  resetConnection
} = connectionSlice.actions;

export default connectionSlice.reducer;